/**
 * Searchable interface for patient data
 * These fields are used by the patient search function
 */
export interface SearchablePatientData {
  userFirstName?: string;
  userSecondName?: string;
  userLBO?: string;
  note?: string;
}

/**
 * Searchable interface for article data
 * Supports DUAL SEARCH: both article names and patient information
 */
export interface SearchableArticleData {
  articleName?: string;
  patientName?: string;
  patientLBO?: string;
  note?: string;
}

/**
 * Searchable interface for aggregated data
 * Only supports article name search
 */
export interface SearchableAggregatedData {
  articleName?: string;
}

export const filterByNameAndLBO = <T extends SearchablePatientData>(
  search: string,
  data: T[] = []
): T[] => {
  if (!search || !data) return data;

  // Split search terms by whitespace and filter out empty strings
  const searchTerms = search
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter((term) => term.length > 0);

  if (searchTerms.length === 0) return data;

  return data.filter((item) => {
    if (!item) return false;

    const userFirstName = (item.userFirstName || "").toLowerCase();
    const userSecondName = (item.userSecondName || "").toLowerCase();
    const userLBO = (item.userLBO || "").toLowerCase();
    const note = (item.note || "").toLowerCase();

    // Create searchable text by combining all fields
    const searchableText =
      `${userFirstName} ${userSecondName} ${userLBO} ${note}`.toLowerCase();

    // For single search term, use includes for partial matching
    if (searchTerms.length === 1) {
      return searchableText.includes(searchTerms[0]);
    }

    // For multiple search terms, all terms must be found in the combined text
    return searchTerms.every((term) => searchableText.includes(term));
  });
};

/**
 * Filters article data by article name and optionally patient information
 */
export const filterArticlesByName = <T extends SearchableArticleData>(
  search: string,
  data: T[] = []
): T[] => {
  if (!search || !data) return data;

  const searchTerms = search
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter((term) => term.length > 0);

  if (searchTerms.length === 0) return data;

  return data.filter((item) => {
    if (!item) return false;

    const articleName = (item.articleName || "").toLowerCase();
    const patientName = (item.patientName || "").toLowerCase();
    const patientLBO = (item.patientLBO || "").toLowerCase();
    const note = (item.note || "").toLowerCase();

    // Create searchable text - prioritize article name, include patient info
    const searchableText =
      `${articleName} ${patientName} ${patientLBO} ${note}`.toLowerCase();

    // For single search term, use includes for partial matching
    if (searchTerms.length === 1) {
      return searchableText.includes(searchTerms[0]);
    }

    // For multiple search terms, all terms must be found in the combined text
    return searchTerms.every((term) => searchableText.includes(term));
  });
};

/**
 * Filters aggregated data by article name only
 */
export const filterAggregatedByName = <T extends SearchableAggregatedData>(
  search: string,
  data: T[] = []
): T[] => {
  if (!search || !data) return data;

  const searchTerms = search
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter((term) => term.length > 0);

  if (searchTerms.length === 0) return data;

  return data.filter((item) => {
    if (!item) return false;

    const articleName = (item.articleName || "").toLowerCase();

    // For single search term, use includes for partial matching
    if (searchTerms.length === 1) {
      return articleName.includes(searchTerms[0]);
    }

    // For multiple search terms, all terms must be found in article name
    return searchTerms.every((term) => articleName.includes(term));
  });
};
